import {GlobalErrorContext, extractErrorDetails, ErrorDetails} from 'error/context/GlobalErrorContext';
import {Alert, Pane} from 'evergreen-ui';
import React from 'react';
import {Subject} from 'rxjs';
import {captureError} from 'monitoring';


export class GlobalErrorBoundary extends React.Component<{}, { hasError: boolean, isSessionTimeout: boolean, authProvider?: 'okta' | 'azure', errorDetails?: ErrorDetails }> {

    private oktaSessionTimeoutErrorMsgs = [
        "OAuthError: The client specified not to prompt, but the user is not logged in.",
        "AuthSdkError: The JWT expired and is no longer valid",
    ];
    
    private azureSessionTimeoutErrorMsgs = [
        "BrowserAuthError: token_expired",
        "BrowserAuthError: interaction_in_progress",
        "InteractionRequiredAuthError",
    ];

    private originalHandler: any = null;
    private readonly globalError$ = new Subject<Error>();

    constructor(props: object) {
        super(props);
        this.state = {
            hasError: false,
            isSessionTimeout: false,
            authProvider: undefined,
        };
    }

    static getDerivedStateFromError(error: any) {
        return {
            hasError: true,
        };
    }

    componentDidCatch(error: any, errorInfo: any) {
        const errorDetails = extractErrorDetails(error);
        captureError(error, errorInfo);
        this.setState({ errorDetails });
    }

    componentDidMount() {
        this.originalHandler = window.onunhandledrejection;

        //catch promise error in your component, don't let it bubble up here my dudes
        window.onunhandledrejection = (event: PromiseRejectionEvent) => {
            // looks like we have some potential listeners, maybe let them handle the error
            if (this.globalError$.observers.length > 0) {
                this.globalError$.next(event.reason);
            } else {
                let isSessionTimeout = false;
                let authProvider: 'okta' | 'azure' | undefined = undefined;
                let errorDetails = extractErrorDetails(event.reason);
                
                if (event.reason) {
                    const errorString = event.reason.toString().trim();
                    
                    if (this.oktaSessionTimeoutErrorMsgs.some(msg => errorString.includes(msg))) {
                        isSessionTimeout = true;
                        authProvider = 'okta';
                    } else if (this.azureSessionTimeoutErrorMsgs.some(msg => errorString.includes(msg))) {
                        isSessionTimeout = true;
                        authProvider = 'azure';
                    }
                }
                
                this.setState({
                    hasError: true,
                    isSessionTimeout: isSessionTimeout,
                    authProvider: authProvider,
                    errorDetails: errorDetails,
                });
            }

            // other libs might set their own handlers, e.g. datadog
            if (typeof this.originalHandler === 'function') {
                this.originalHandler.apply(window, arguments);
            }
        }
    }

    componentWillUnmount() {
        window.onunhandledrejection = this.originalHandler;
    }

    render() {
        if (this.state.hasError) {
            if (this.state.isSessionTimeout) {
                const authProvider = this.state.authProvider;
                const message = authProvider === 'azure' 
                    ? 'Your Azure session has timed-out. Please log in again.'
                    : 'Your session has timed-out. Please log in again.';
                    
                return (
                    <Pane>
                        <Alert intent='danger' title='Session timeout'>
                            {message}
                        </Alert>
                    </Pane>
                );
            }

            // Extract user-friendly error message
            let errorMessage = 'An error has occurred somewhere in the system and we\'re unable to correct it.';
            let technicalDetails: string | undefined = undefined;

            if (this.state.errorDetails) {
                // Use the extracted message for user display
                errorMessage = this.state.errorDetails.message;
                
                // Prepare technical details for development
                if (process.env.NODE_ENV === 'development') {
                    const details: string[] = [];
                    if (this.state.errorDetails.stack) {
                        details.push(`Stack: ${this.state.errorDetails.stack}`);
                    }
                    if (this.state.errorDetails.networkError) {
                        details.push(`Network Error: ${JSON.stringify(this.state.errorDetails.networkError, null, 2)}`);
                    }
                    if (this.state.errorDetails.graphQLErrors && this.state.errorDetails.graphQLErrors.length > 0) {
                        details.push(`GraphQL Errors: ${JSON.stringify(this.state.errorDetails.graphQLErrors, null, 2)}`);
                    }
                    technicalDetails = details.join('\n\n');
                }
            }

            return (
                <Pane>
                    <Alert intent='danger' title='Something went wrong'>
                        {errorMessage}
                        <Pane marginTop={8}>
                            Please refresh the page and try again. If the problem persists, contact support.
                        </Pane>
                        {technicalDetails && (
                            <Pane marginTop={16}>
                                <details>
                                    <summary style={{ cursor: 'pointer', fontSize: '12px', opacity: 0.7 }}>
                                        Technical Details (Development Only)
                                    </summary>
                                    <pre style={{ 
                                        fontSize: '11px', 
                                        marginTop: '8px', 
                                        padding: '8px', 
                                        backgroundColor: '#f5f5f5', 
                                        overflow: 'auto',
                                        maxHeight: '200px'
                                    }}>
                                        {technicalDetails}
                                    </pre>
                                </details>
                            </Pane>
                        )}
                    </Alert>
                </Pane>
            );
        }

        return (
            <GlobalErrorContext.Provider value={this.globalError$}>
                {this.props.children}
            </GlobalErrorContext.Provider>
        );
    }
}
